/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo, useState } from 'react'
import css from '@emotion/css/macro'
import {
  GeoapifyGeocoderAutocomplete,
  GeoapifyContext,
} from '@geoapify/react-geocoder-autocomplete'
import '@geoapify/geocoder-autocomplete/styles/round-borders-dark.css'
import colors from '@sportninja/common/constants/appColors'
import { Input } from '../Input'
import { useForm } from 'react-hook-form'
import { Button } from '../Button'
import Icon from '../Icon'

type AmenityProperties = {
  // street: string
  address_line2: string
  name: string
  city: string
  country: string
  country_code: string
  housenumber: string
  lat: number
  lon: number
  postcode: string
  state: string
  state_code: string
  suburb: string
  timezone: {
    name: string
    offset_DST: string
  }
}

type InputFields = {
  address_line1: string
  address_line2: string
  city: string
  postcode: string
  country: string
  state: string
}

type ReturnArgs = InputFields & {
  timezone: string
  name: string
  latitude: number
  longitude: number
  state_code: string
}

type Facility = {
  id?: string
  name: string
}

interface Props {
  initialState?: ReturnArgs
  initialFacilities?: Facility[]
  onCancel: () => void
  onSubmit: ({
    args,
    facilities,
  }: {
    args: ReturnArgs
    facilities: Facility[]
  }) => void
}

export const NewLocationAddressSelector = ({
  onCancel,
  onSubmit,
  initialState,
  initialFacilities = [],
}: Props) => {
  const [value, setValue] = useState(initialState?.name || '')
  const [timezone, setTimezone] = useState(initialState?.timezone || '')
  const [newFacilityName, setNewFacilityName] = useState('')

  const getLatLong = useMemo(() => {
    if (initialState?.latitude && initialState?.longitude) {
      return [initialState?.latitude, initialState?.longitude]
    }

    return []
  }, [initialState?.latitude, initialState?.longitude])

  const [latLong, setLatLong] = useState<number[]>(getLatLong)
  const [facilities, setFacilities] = useState<Facility[]>([])
  const [showNewFacilityInput, setShowNewFacilityInput] = useState(false)
  const [editFacility, setEditFacility] = useState(false)
  const [stateCode, setStateCode] = useState('')
  const [facilityToEdit, setFacilityToEdit] = useState<Facility>({
    name: '',
    id: undefined,
  })

  useEffect(() => {
    if (initialFacilities?.length > 0) {
      initialFacilities.forEach((facility) => {
        setFacilities((prev) => [...prev, facility])
      })
    }
  }, [initialFacilities])

  const {
    control,
    setValue: setFormValue,
    handleSubmit,
  } = useForm<InputFields>({
    defaultValues: {
      address_line1: initialState?.address_line1 || '',
      address_line2: initialState?.address_line2 || '',
      city: initialState?.city || '',
      country: initialState?.country || '',
      postcode: initialState?.postcode || '',
      state: initialState?.state || '',
    },
  })

  const onSubmitPressed = (data: InputFields) => {
    const args: ReturnArgs = {
      ...data,
      name: value,
      timezone,
      latitude: latLong[0],
      longitude: latLong[1],
      state_code: stateCode,
    }
    onSubmit({
      args,
      facilities,
    })
  }

  const onError = (error) => {
    console.error(error)
  }

  const onCancelPressed = () => {
    if (value && !initialState) {
      setValue('')
      setFormValue('address_line1', '')
      setFormValue('address_line2', '')
      setFormValue('city', '')
      setFormValue('country', '')
      setFormValue('postcode', '')
      setFormValue('state', '')
    } else {
      onCancel()
    }
  }

  const onPlaceSelect = useCallback(
    (result: { properties: AmenityProperties }) => {
      setValue(result?.properties?.name)

      setFormValue(
        'address_line1',
        result?.properties?.address_line2.split(',')[0]
      )
      setFormValue('address_line2', result?.properties?.suburb)
      setFormValue('city', result?.properties?.city)
      setFormValue('country', result?.properties?.country)
      setFormValue('postcode', result?.properties?.postcode)
      setFormValue('state', result?.properties?.state)

      setStateCode(result?.properties?.state_code)
      setTimezone(result?.properties?.timezone?.name)
      setLatLong([result?.properties?.lat, result?.properties?.lon])
    },
    [value]
  )

  return (
    <GeoapifyContext apiKey='197a6aacb8fc4fad8e90cdfde2bbf2de'>
      <form
        onSubmit={handleSubmit(onSubmitPressed, onError)}
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          flex: 1;
        `}
      >
        <div
          css={css`
            display: flex;
            flex: 1;
            flex-direction: column;
          `}
        >
          <div>
            <label>
              <p
                css={css`
                  font-weight: 500;
                  font-size: 12px;
                  line-height: 12px;
                  color: ${colors.WHITE};
                  margin-bottom: 8px;
                `}
              >
                Name *
              </p>
            </label>
            <div
              css={css`
                display: flex;
                flex: 1;
                align-items: center;
                justify-content: space-between;
                max-height: 40px;
                transition: all 0.1s ease-in-out;
                margin-bottom: 16px;

                .geocoder-container {
                  flex: 1;
                  width: 100%;
                  display: flex;
                }

                .geoapify-autocomplete-input {
                  box-sizing: border-box;
                  margin: 0;
                  padding: 4px 11px px;
                  color: rgba(255, 255, 255, 0.85);
                  font-size: 14px;
                  line-height: 1.5714285714285714;
                  list-style: none;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI',
                    Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif,
                    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
                    'Noto Color Emoji';
                  position: relative;
                  display: inline-block;
                  width: 100%;
                  min-width: 0;
                  background-color: transparent;
                  background-image: none;
                  border-width: 1px;
                  border-style: solid;
                  border-color: #3f4753;
                  border-radius: 4px;
                  transition: all 0.2s;
                  height: 40px;
                }

                .geoapify-autocomplete-input:focus-within {
                  border-color: ${colors.PRIMARY_LIGHT};
                }

                .geoapify-autocomplete-input:hover {
                  border-color: ${colors.PRIMARY_LIGHT};
                }

                .geoapify-close-button {
                  display: none;
                }

                .geoapify-autocomplete-item {
                  gap: 6px;
                }

                .geoapify-autocomplete-items {
                  box-shadow: none;
                }
              `}
            >
              <GeoapifyGeocoderAutocomplete
                placeholder='Enter the amenity name'
                value={value}
                type='amenity'
                lang='en'
                placeSelect={onPlaceSelect}
              />
            </div>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-bottom: 8px;
            `}
          >
            <Input
              fieldName='address_line1'
              control={control}
              label='Address Line 1'
            />
            <Input
              fieldName='address_line2'
              control={control}
              label='Address Line 2'
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-bottom: 8px;
            `}
          >
            <Input fieldName='city' control={control} label='City' />
            <Input fieldName='postcode' control={control} label='Postal Code' />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              margin-bottom: 8px;
            `}
          >
            <Input fieldName='country' control={control} label='Country' />
            <Input fieldName='state' control={control} label='Province/State' />
          </div>
        </div>
        <div
          css={css`
            padding: 16px 0;
            border-top: 1px solid ${colors.SOFT_STEEL};
            margin-top: 16px;
            border-bottom: 1px solid ${colors.SOFT_STEEL};
            margin-bottom: 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            {facilities.map((item, index) => (
              <div
                key={index.toString()}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding: ${editFacility && facilityToEdit.id === item.id
                    ? '8px 0'
                    : '8px'};
                  border-radius: 4px;
                  background-color: ${index % 2 === 0
                    ? 'transparent'
                    : editFacility && facilityToEdit.id === item.id
                    ? 'transparent'
                    : colors.HEADER};
                `}
              >
                {editFacility && facilityToEdit.id === item.id ? (
                  <div
                    css={css`
                      display: flex;
                      flex: 1;
                      align-items: center;
                      justify-content: space-between;
                      border-width: 1px;
                      border-style: solid;
                      border-color: ${colors.SOFT_STEEL};
                      border-radius: 4px;
                      padding: 8px;
                      max-height: 40px;
                      transition: all 0.1s ease-in-out;

                      &:focus-within {
                        border-color: ${colors.WHITE};
                      }
                    `}
                  >
                    <input
                      css={css`
                        display: flex;
                        border: none;
                        width: 100%;
                        flex: 1;
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 16px;
                        color: ${colors.WHITE};
                        background-color: transparent;
                      `}
                      name='facilities-input-creator'
                      type='text'
                      value={facilityToEdit.name}
                      onBlur={() => {
                        setEditFacility(false)
                        setFacilityToEdit({
                          name: '',
                          id: undefined,
                        })
                      }}
                      placeholder='Enter the facility name'
                      onChange={(e) =>
                        setFacilityToEdit({
                          ...facilityToEdit,
                          name: e.target.value,
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()

                          const newFacilitiesArray = facilities.filter(
                            (_, i) => i !== index
                          )
                          newFacilitiesArray.push(facilityToEdit)
                          setEditFacility(false)
                          setFacilities(newFacilitiesArray)
                          setFacilityToEdit({
                            name: '',
                            id: undefined,
                          })
                        }

                        if (e.key === 'Escape') {
                          setEditFacility(false)
                          setFacilityToEdit({
                            name: '',
                            id: undefined,
                          })
                        }
                      }}
                    />
                    <button
                      onClick={(e) => {
                        e.preventDefault()

                        setEditFacility(false)
                        setFacilityToEdit({
                          name: '',
                          id: undefined,
                        })
                      }}
                    >
                      <Icon
                        name='times'
                        color={colors.DEFAULT_FLAIR}
                        fontSize={18}
                      />
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      onClick={() => {
                        setFacilityToEdit(item)
                        setEditFacility(true)
                      }}
                      css={css`
                        display: flex;
                        flex: 1;
                        padding: 8px 0;
                      `}
                    >
                      <span>{item.name}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault()

                        setFacilities((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }}
                    >
                      <Icon name='trash' color={colors.DEFAULT_FLAIR} />
                    </button>
                  </>
                )}
              </div>
            ))}
            {showNewFacilityInput && (
              <div
                css={css`
                  display: flex;
                  flex: 1;
                  align-items: center;
                  justify-content: space-between;
                  border-width: 1px;
                  border-style: solid;
                  border-color: ${colors.SOFT_STEEL};
                  border-radius: 4px;
                  padding: 8px;
                  max-height: 40px;
                  transition: all 0.1s ease-in-out;
                  margin-top: 16px;

                  &:focus-within {
                    border-color: ${colors.WHITE};
                  }
                `}
              >
                <input
                  css={css`
                    display: flex;
                    border: none;
                    width: 100%;
                    flex: 1;
                    font-weight: 500;
                    font-size: 16px;
                    line-height: 16px;
                    color: ${colors.WHITE};
                    background-color: transparent;
                  `}
                  name='facilities-input-creator'
                  type='text'
                  value={newFacilityName}
                  placeholder='Enter the facility name'
                  onChange={(e) => setNewFacilityName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()

                      setShowNewFacilityInput(false)
                      setFacilities((prev) => [
                        ...prev,
                        { name: newFacilityName, id: '' },
                      ])
                      setNewFacilityName('')
                    }
                  }}
                />
                <button
                  type='reset'
                  onClick={(e) => {
                    e.preventDefault()

                    setShowNewFacilityInput(false)
                    setNewFacilityName('')
                  }}
                >
                  <Icon
                    name='times'
                    color={colors.ATTENDANCE_GRAY}
                    fontSize={18}
                  />
                </button>
              </div>
            )}
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: flex-end;
                margin-top: ${facilities.length > 0 || showNewFacilityInput
                  ? '16px'
                  : '0'};
              `}
            >
              <Button
                onClick={(e) => {
                  e.preventDefault()
                  setShowNewFacilityInput(true)
                }}
                label='+ Add Facility'
                variant='secondary'
              />
            </div>
          </div>
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 20px;
          `}
        >
          <Button
            onClick={(e) => {
              e.preventDefault()

              onCancelPressed()
            }}
            label={value && !initialState ? 'Clear' : 'Cancel'}
            variant='secondary'
          />
          <Button
            onClick={handleSubmit(onSubmitPressed, onError)}
            type='submit'
            label='Submit'
          />
        </div>
      </form>
    </GeoapifyContext>
  )
}
